// ============================================================================
// Browse / Dictionary (spec §10.5.4). A study-first surface: skim the Hanja and
// words (hangul + English + romanization), or search across the FULL set via
// the server-side search RPC. Open one to study it in depth.
//
// Listing is SERVER-SIDE PAGINATED (api.pageCharacters / api.pageWords via
// range()) so the DOM and network stay bounded as the word set grows — only one
// page of cards is ever fetched/rendered. Search bypasses paging (it's already a
// narrowing action) and shows the RPC's result set directly.
// ============================================================================

import { el, mount } from '../dom.js';
import * as api from '@core/data/api.js';
import { romanize } from '@core/hangul/romanize.js';
import { primaryEum, primaryReading } from '../games/shared.js';
import { romanizationOn } from '../prefs.js';

const PAGE_SIZE = 60; // cards per page

export function renderBrowse({ navigate }) {
  const container = el('div.container.stack');

  const state = {
    tab: 'characters', // characters | words | advanced
    advKind: 'words',  // within the Advanced tab: words | characters
    page: 0,           // 0-based page for the active tab
    rows: [],          // current page's rows
    total: 0,          // total rows for the active view (respects the active filter)
    counts: { coreChars: 0, coreWords: 0, advWords: 0, rareChars: 0 },
    topik: null,       // Words filter: TOPIK level (null = all)
    tier: null,        // Hanja filter: edu_tier (null = all)
    hasWords: false,   // Hanja filter: only characters that build >=1 word
    topikOptions: [],  // TOPIK levels present in the data
    query: '',
    searchResults: null, // null = browsing; array = search mode
    loading: false,
    error: null,
  };
  // Is the active view showing characters (vs words)?
  const showingChars = () => state.tab === 'characters' || (state.tab === 'advanced' && state.advKind === 'characters');
  let searchTimer = null;

  // --- card renderers -------------------------------------------------------
  function charCard(c) {
    const eum = primaryEum(c);
    const wc = api.characterWordCount(c);
    return el(`button.card.browse-card${wc ? '' : '.browse-card--empty'}`, { onclick: () => navigate(`#/item/character/${c.id}`) }, [
      el('div.browse-card__glyph', { text: c.char }),
      el('div.stack', { style: 'gap:2px' }, [
        el('div.browse-card__reading', { text: primaryReading(c) || eum }),
        eum && romanizationOn() ? el('div.faint', { text: romanize(eum) }) : false,
        el('div.muted', { text: c.meaning_en || '—' }),
        el('div.faint', { text: wc ? `${wc} word${wc === 1 ? '' : 's'}` : 'no words yet' }),
      ]),
    ]);
  }

  function wordCard(w) {
    return el('button.card.browse-card', { onclick: () => navigate(`#/item/word/${w.id}`) }, [
      el('div.browse-card__glyph.browse-card__glyph--ko', { text: w.hangul }),
      el('div.stack', { style: 'gap:2px' }, [
        el('div.browse-card__reading', { text: w.hanja_form || '' }),
        romanizationOn() ? el('div.faint', { text: w.romanization || romanize(w.hangul) }) : false,
        el('div.muted', { text: w.meaning_en || '—' }),
      ]),
    ]);
  }

  function resultCard(r) {
    return el('button.card.browse-card', { onclick: () => navigate(`#/item/${r.kind}/${r.id}`) }, [
      el('div', { class: r.kind === 'character' ? 'browse-card__glyph' : 'browse-card__glyph browse-card__glyph--ko', text: r.title }),
      el('div.stack', { style: 'gap:2px' }, [
        el('div.muted', { text: r.subtitle || '' }),
        el('div.faint', { text: r.kind }),
      ]),
    ]);
  }

  // --- search (bypasses paging) --------------------------------------------
  const search = el('input.input', {
    type: 'search',
    placeholder: 'Search all Hanja + words by character, reading, meaning…',
    oninput: (e) => {
      state.query = e.target.value.trim();
      clearTimeout(searchTimer);
      if (!state.query) { state.searchResults = null; return refresh(); }
      searchTimer = setTimeout(runSearch, 220);
    },
  });

  async function runSearch() {
    try {
      const rows = await api.search(state.query);
      state.searchResults = Array.isArray(rows) ? rows : [];
    } catch {
      state.searchResults = [];
    }
    refresh();
  }

  // --- paged listing --------------------------------------------------------
  async function loadPage() {
    state.loading = true; state.error = null; refresh();
    try {
      const opts = { offset: state.page * PAGE_SIZE, limit: PAGE_SIZE };
      const advanced = state.tab === 'advanced';
      const { rows, count } = showingChars()
        ? await api.pageCharacters({ ...opts, tier: advanced ? null : state.tier, hasWords: advanced ? false : state.hasWords, rare: advanced })
        : await api.pageWords({ ...opts, topikLevel: advanced ? null : state.topik, advanced });
      state.rows = rows;
      state.total = count; // filtered total drives the pager
    } catch (e) {
      state.rows = []; state.error = e.message || 'Could not load content';
    } finally {
      state.loading = false; refresh();
    }
  }

  function totalPages() {
    return Math.max(1, Math.ceil(state.total / PAGE_SIZE));
  }

  function gotoPage(p) {
    const np = Math.min(Math.max(0, p), totalPages() - 1);
    if (np === state.page) return;
    state.page = np;
    loadPage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function setTab(tab) {
    if (state.tab === tab && state.searchResults === null) return;
    state.tab = tab;
    if (tab === 'advanced') state.advKind = 'words';
    state.page = 0;
    state.searchResults = null;
    state.query = '';
    search.value = '';
    loadPage();
  }

  function setAdvKind(kind) {
    if (state.advKind === kind) return;
    state.advKind = kind;
    state.page = 0;
    loadPage();
  }

  function setFilter(value) {
    if (state.tab === 'characters') state.tier = value || null;
    else state.topik = value ? Number(value) : null;
    state.page = 0;
    loadPage();
  }

  // --- rendering ------------------------------------------------------------
  function grid() {
    const g = el('div.browse-grid');
    if (state.searchResults !== null) {
      if (!state.searchResults.length) g.append(el('p.muted', { text: `No matches for “${state.query}”.` }));
      else state.searchResults.forEach((r) => g.append(resultCard(r)));
      return g;
    }
    if (state.error) { g.append(el('div.banner.banner--warn', { text: state.error })); return g; }
    const render = showingChars() ? charCard : wordCard;
    if (!state.rows.length) g.append(el('p.muted', { text: 'Nothing to show.' }));
    else state.rows.forEach((row) => g.append(render(row)));
    return g;
  }

  function tabBtn(id, label) {
    return el(`button.btn${state.tab === id && state.searchResults === null ? '.btn--primary' : ''}`, {
      text: label, onclick: () => setTab(id),
    });
  }

  function filterControl() {
    if (state.searchResults !== null) return false;
    const opt = (value, label, selected) => el('option', { value, text: label, selected });
    // Advanced tab: a Words/Hanja sub-toggle instead of the core filters.
    if (state.tab === 'advanced') {
      const kindBtn = (kind, label) => el(`button.btn${state.advKind === kind ? '.btn--primary' : ''}`, { text: label, onclick: () => setAdvKind(kind) });
      return el('div.row', { style: 'gap:var(--space-2);align-items:center' }, [
        el('span.faint', { text: 'Show:' }),
        kindBtn('words', `Words (${state.counts.advWords})`),
        kindBtn('characters', `Hanja (${state.counts.rareChars})`),
      ]);
    }
    if (state.tab === 'words') {
      if (!state.topikOptions.length) return false;
      return el('select.input.browse-filter', { 'aria-label': 'Filter by TOPIK level', onchange: (e) => setFilter(e.target.value) }, [
        opt('', 'All TOPIK levels', state.topik == null),
        ...state.topikOptions.map((l) => opt(String(l), `TOPIK ${l}`, state.topik === l)),
      ]);
    }
    // Characters: tier select + a "only characters with words" toggle.
    const tierSel = el('select.input.browse-filter', { 'aria-label': 'Filter by school tier', onchange: (e) => setFilter(e.target.value) }, [
      opt('', 'All tiers', state.tier == null),
      opt('middle', 'Middle school', state.tier === 'middle'),
      opt('high', 'High school', state.tier === 'high'),
    ]);
    const wordsToggle = el('label.row.browse-haswords', { style: 'gap:6px;align-items:center;white-space:nowrap', title: 'Show only characters that build at least one word in the set' }, [
      el('input', { type: 'checkbox', checked: state.hasWords, onchange: (e) => { state.hasWords = e.target.checked; state.page = 0; loadPage(); } }),
      el('span', { text: 'Only with words' }),
    ]);
    return el('div.row', { style: 'gap:var(--space-3);flex-wrap:wrap;align-items:center' }, [tierSel, wordsToggle]);
  }

  function hint() {
    if (state.searchResults !== null) {
      return el('p.faint', { text: `${state.searchResults.length} result${state.searchResults.length === 1 ? '' : 's'}` });
    }
    if (state.tab === 'advanced') {
      return el('p.faint', { text: `Advanced / rare — kept out of the core lists · ${state.total} ${showingChars() ? 'Hanja' : 'words'}` });
    }
    const parts = [];
    if (state.tab === 'words' && state.topik != null) parts.push(`TOPIK ${state.topik}`);
    if (state.tab === 'characters' && state.tier) parts.push(state.tier === 'middle' ? 'Middle school' : 'High school');
    if (state.tab === 'characters' && state.hasWords) parts.push('with words');
    if (parts.length) return el('p.faint', { text: `Filtered: ${parts.join(' · ')} · ${state.total} ${showingChars() ? 'Hanja' : 'words'}` });
    return false;
  }

  function pager() {
    if (state.searchResults !== null || state.error) return false;
    const pages = totalPages();
    const from = state.total ? state.page * PAGE_SIZE + 1 : 0;
    const to = Math.min(state.total, (state.page + 1) * PAGE_SIZE);
    return el('div.row.browse-pager', { style: 'justify-content:space-between;align-items:center;gap:var(--space-3)' }, [
      el('button.btn', { text: '← Prev', disabled: state.page <= 0 || state.loading, onclick: () => gotoPage(state.page - 1) }),
      el('span.faint', { text: `${from}–${to} of ${state.total} · page ${state.page + 1} / ${pages}` }),
      el('button.btn', { text: 'Next →', disabled: state.page >= pages - 1 || state.loading, onclick: () => gotoPage(state.page + 1) }),
    ]);
  }

  function refresh() {
    mount(container,
      el('div.stack', {}, [
        el('div.row', { style: 'flex-wrap:wrap' }, [el('h1', { text: 'Browse' }), el('div.appbar__spacer'),
          tabBtn('characters', `Hanja (${state.counts.coreChars || '…'})`),
          tabBtn('words', `Words (${state.counts.coreWords || '…'})`),
          tabBtn('advanced', `Advanced (${(state.counts.advWords + state.counts.rareChars) || '…'})`),
          el('button.btn', { text: 'By reading', title: 'Browse Hanja that share a sound-reading', onclick: () => navigate('#/reading') })]),
        el('div.row', { style: 'gap:var(--space-2)' }, [search, filterControl() || false]),
        hint(),
        state.loading ? el('div.card', { text: 'Loading…' }) : grid(),
        pager() || false,
      ])
    );
  }

  // --- boot: seed tab-label counts, then load the first page ---------------
  (async () => {
    mount(container, el('div.card', { text: 'Loading dictionary…' }));
    const [counts, topik] = await Promise.all([
      api.contentCounts().catch(() => state.counts),
      api.topikLevels().catch(() => []),
    ]);
    state.counts = counts;
    state.topikOptions = topik;
    loadPage();
  })();

  return container;
}
